import * as React from "react"

import { cn } from "@/lib/utils"

const Table = React.forwardRef(({ className, ...props }, ref) => {
  const id = React.useId(), wrapper = React.useRef(null);
  const [overflowing, setOverflowing] = React.useState(false);
  React.useEffect(() => {
    const node = wrapper.current;
    let frame;
    const measure = () => {
      if (!node.isConnected) return;
      const table = node.querySelector("table");
      setOverflowing(Math.max(node.scrollWidth, table?.scrollWidth || 0, table?.getBoundingClientRect().width || 0) > node.clientWidth + 1);
    };
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure); };
    const observer = new ResizeObserver(schedule);
    const changes = new MutationObserver(schedule);
    observer.observe(node);
    if (node.firstElementChild) observer.observe(node.firstElementChild);
    changes.observe(node, { childList: true, characterData: true, subtree: true });
    document.fonts?.ready.then(schedule);
    schedule();
    return () => { observer.disconnect(); changes.disconnect(); cancelAnimationFrame(frame); };
  }, [props.children]);
  return <div className="min-w-0 max-w-full">
    {overflowing && <p className="app-table-scroll-hint" data-testid={props["data-testid"] ? `${props["data-testid"]}-scroll-hint` : `table-hint-${id}`}>Scorri orizzontalmente per vedere tutte le colonne</p>}
    <div ref={wrapper} className="app-table-scroll relative w-full overflow-auto" role="region" aria-label="Tabella scorrevole" tabIndex={0} data-testid={props["data-testid"] ? `${props["data-testid"]}-scroll` : `table-scroll-${id}`}>
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-sm", className)}
      {...props} />
  </div></div>;
})
Table.displayName = "Table"

const TableHeader = React.forwardRef(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props} />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)}
    {...props} />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
      className
    )}
    {...props} />
))
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-11 bg-muted/40 px-3 py-3 text-left align-middle text-xs leading-snug font-semibold text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props} />
))
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "px-3 py-3 align-top leading-relaxed [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props} />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props} />
))
TableCaption.displayName = "TableCaption"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
